import CheckoutAccess from '@ui/Accommodation/CheckoutAccess/component'
import BookingReward from '@ui/Accommodation/Offer/BookingReward/component'
import Flags from '@ui/Flags/component'
import SidePanel from '@ui/SidePanel/component'
import * as coreCommon from 'assets/core/js/common'
import { hideLoading } from 'assets/core/js/module/buttonLoading'
import ImagesSlider from 'assets/core/js/module/imagesSlider'
import mobileBottomPanel from 'assets/core/js/module/mobileBottomPanel'
import almaWidgets from 'assets/core/js/module/payment/alma/widgets'
import paypalPayLater from 'assets/core/js/module/payment/paypal/payLater'
import SliderManager from 'assets/core/js/module/sliderManager'
import stickyObserver from 'assets/core/js/module/stickyObserver'
import { initZoomMediasPanel } from 'assets/core/js/page/product/zoomMediasPanel'
import type { SidePanelConfig } from '@design/objects/sidePanel/twig/assets/index'
import type { SidePanelType } from '@ui/SidePanel/component'
import type { Swiper } from 'swiper'

export const createSidePanel = (id: string, url: string, type: 'accommodation' | 'pitch', accommodationId: string): SidePanelType | undefined => {
  const sidePanelConfig: SidePanelConfig = {
    rootEl: document.body,
    onOpen: function () {
      document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.opened', { detail: { accommodationId } }))
    },
    onClose: function (sidePanel: SidePanelType) {
      document.body.dispatchEvent(new CustomEvent('sidePanelAccommodation.modal.closed'))

      const checkOfferForm = sidePanel.getElement().querySelector<HTMLFormElement>('form[data-check-offer-id]')

      if (checkOfferForm) {
        const submitEl = checkOfferForm.querySelector<HTMLButtonElement>('[type="submit"]')

        if (!submitEl) {
          return
        }

        if (!submitEl.classList.contains('o-button--with-loading')) {
          return
        }

        hideLoading(submitEl)
      }
    },
    onContentSet: function (sidePanel) {
      const images = sidePanel.getElement().querySelectorAll<HTMLImageElement>('.product__images-mosaic img')
      const totalProductImages = images.length

      if (totalProductImages && totalProductImages > 0) {
        ImagesSlider(sidePanel.getElement().querySelector('.product__images-mosaic .swiper') as HTMLElement, {
          loop: false,
          pagination: {
            el: '.swiper-pagination',
            type: 'custom',
            renderCustom: function (_swiper: Swiper, current: number, total: number) {
              return `${current}/${total}`
            },
          },
          spaceBetween: 10,
        })

        images.forEach((img) => {
          initZoomMediasPanel(img, true)
        })
      }

      const checkOfferForm = sidePanel.getElement().querySelector<HTMLFormElement>('form[data-check-offer-id]')

      if (checkOfferForm) {
        CheckoutAccess(checkOfferForm)

        checkOfferForm.addEventListener('submit', function () {
          const event = new CustomEvent('checkOfferForm.submit', {
            detail: {
              accommodationId,
              flexible: {
                selected: false,
                amount: 0,
              },
            },
          })

          document.body.dispatchEvent(event)
        })
      }

      mobileBottomPanel('.o-side-panel .dca-mobile-bottompanel__toggle')

      const accommodationFlags = sidePanel.getElement().querySelector<HTMLElement>('.side-panel__accommodation-flags')
      if (accommodationFlags) {
        Flags(accommodationFlags)
      }

      const offerRewardSimulation = sidePanel.getElement().querySelectorAll<HTMLElement>('.accommodation-offer-booking-reward')

      offerRewardSimulation.forEach((el) => {
        BookingReward(el)
      })
    },
  }

  const templateEl = document.getElementById(`${id}-template`) as HTMLElement

  if (!templateEl) {
    return
  }

  const sidePanelEl = templateEl.parentNode?.insertBefore<HTMLElement>(coreCommon.createNodeFromHTML(templateEl.innerHTML), templateEl)

  if (!sidePanelEl) {
    return
  }

  const sidePanel = SidePanel(sidePanelEl, sidePanelConfig)

  if (!sidePanel) {
    return
  }

  templateEl.remove()

  const xhrRequest = coreCommon.createXhr('GET', url)

  xhrRequest.onreadystatechange = () => {
    if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 200 && xhrRequest.response) {
      sidePanel.setContent(xhrRequest.response as string)
      initOptionsSlider(sidePanelEl)
      initScrollObserver(sidePanelEl)

      if (type === 'pitch') {
        stickyObserver()
      } else if (type === 'accommodation') {
        initScrollToAvailability(sidePanelEl)
        initPaypalPayLater(sidePanelEl)
        initAlmaWidgets(sidePanelEl)
      }
    } else if (xhrRequest.readyState === XMLHttpRequest.DONE && xhrRequest.status === 500) {
      sidePanel.close()
    }
  }

  xhrRequest.send()

  return sidePanel
}

const initScrollToAvailability = (sidePanelEl: HTMLElement): void => {
  const availabilityPriceEl = sidePanelEl.querySelector<HTMLElement>('.side-panel__bottom .availability__details-prices-price')

  if (!availabilityPriceEl) {
    return
  }

  availabilityPriceEl.addEventListener('click', () => {
    sidePanelEl.querySelector<HTMLElement>('.side-panel__sidebar-block--availability-details')?.scrollIntoView()
  })
}

const initOptionsSlider = (sidePanelEl: HTMLElement): void => {
  const optionsListEl = sidePanelEl.querySelector<HTMLElement>('.accommodation__options-list')
  if (!optionsListEl) {
    return
  }

  SliderManager.initSlider(optionsListEl, {
    loop: false,
    slidesPerView: 'auto',
    navigation: {
      nextEl: optionsListEl.querySelector('.swiper-button-next') as HTMLElement,
      prevEl: optionsListEl.querySelector('.swiper-button-prev') as HTMLElement,
    },
    grabCursor: true,
  })
}

const initPaypalPayLater = (sidePanel: HTMLElement): void => {
  const paypalMessages = sidePanel.querySelectorAll<HTMLElement>('[data-cpp-message]')

  paypalMessages.forEach((paypalMessage) => {
    void paypalPayLater(paypalMessage)
  })
}

const initAlmaWidgets = (sidePanel: HTMLElement): void => {
  const widgets = sidePanel.querySelectorAll<HTMLElement>('[data-alma-widget]')

  widgets.forEach((widget) => {
    void almaWidgets(widget)
  })
}

const initScrollObserver = (sidePanel: HTMLElement): void => {
  const observed = sidePanel.querySelector('.side-panel__accommodation-name')
  const name = sidePanel.querySelector('.side-panel-top__accommodation-info')
  const scrollable = sidePanel.querySelector<HTMLElement>('.side-panel__content-wrapper')

  if (!observed || !name || !scrollable) {
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          name.removeAttribute('hidden')
        } else {
          name.setAttribute('hidden', 'hidden')
        }
      })
    },
    {
      root: scrollable,
      threshold: 0.5,
    }
  )

  observer.observe(observed)
}
